import { useState, useEffect } from "react";
import { adminGetApplicationReport, adminGetDisbursementReport, adminGetSubscriptionReport, adminGetAgentReport, adminGetCollectionReport, adminGetRevenueReport, exportReport } from "@/api";
import { toast } from "sonner";
import { BarChart3, Loader2, Download, IndianRupee, Users, FileText, Banknote, TrendingUp, AlertTriangle, PieChart } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminReports() {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [appReport, setAppReport] = useState(null);
  const [disbReport, setDisbReport] = useState(null);
  const [subReport, setSubReport] = useState(null);
  const [agentReport, setAgentReport] = useState(null);
  const [collectionReport, setCollectionReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, d, s, ag, c, r] = await Promise.all([
        adminGetApplicationReport().catch(() => ({ data: {} })),
        adminGetDisbursementReport().catch(() => ({ data: {} })),
        adminGetSubscriptionReport().catch(() => ({ data: {} })),
        adminGetAgentReport().catch(() => ({ data: {} })),
        adminGetCollectionReport().catch(() => ({ data: {} })),
        adminGetRevenueReport().catch(() => ({ data: {} })),
      ]);
      setAppReport(a.data);
      setDisbReport(d.data);
      setSubReport(s.data);
      setAgentReport(ag.data);
      setCollectionReport(c.data);
      setRevenueReport(r.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const handleExport = async (type, fmt) => {
    try {
      const res = await exportReport(type, fmt);
      const blob = new Blob([res.data], { type: fmt === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} report exported as ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'applications', label: 'Applications', icon: FileText },
    { id: 'revenue', label: 'Revenue', icon: TrendingUp },
    { id: 'collections', label: 'Collections', icon: Banknote },
    { id: 'agents', label: 'Agent Performance', icon: Users },
  ];

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1565C0]" /></div>;
  }

  return (
    <div className="fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Comprehensive business insights and data export</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleExport('applications', 'csv')} variant="outline" size="sm" className="rounded-xl"><Download className="h-3 w-3 mr-1" /> CSV</Button>
          <Button onClick={() => handleExport('applications', 'pdf')} variant="outline" size="sm" className="rounded-xl"><Download className="h-3 w-3 mr-1" /> PDF</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${tab === t.id ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard label="Total Applications" value={appReport?.total || 0} icon={FileText} color="blue" />
            <MetricCard label="Subscription Revenue" value={`₹${parseFloat(subReport?.total_revenue || 0).toLocaleString()}`} icon={IndianRupee} color="green" />
            <MetricCard label="Total Disbursed" value={`₹${parseFloat(disbReport?.total_disbursed || 0).toLocaleString()}`} icon={Banknote} color="purple" />
            <MetricCard label="Collections" value={`₹${parseFloat(collectionReport?.collected || 0).toLocaleString()}`} icon={TrendingUp} color="teal" />
          </div>

          {/* Application Breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-[#0A2540] mb-4">Application Pipeline</h3>
            <div className="grid grid-cols-4 gap-4">
              <StatBar label="Pending" count={appReport?.pending || 0} total={appReport?.total || 1} color="#F59E0B" />
              <StatBar label="Approved" count={appReport?.approved || 0} total={appReport?.total || 1} color="#10B981" />
              <StatBar label="Rejected" count={appReport?.rejected || 0} total={appReport?.total || 1} color="#EF4444" />
              <StatBar label="Disbursed" count={appReport?.disbursed || 0} total={appReport?.total || 1} color="#1565C0" />
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-[#0A2540] mb-4">Export Reports</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['applications', 'disbursements', 'subscriptions', 'users'].map(type => (
                <div key={type} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 capitalize">{type}</p>
                  <div className="flex gap-2">
                    <Button onClick={() => handleExport(type, 'csv')} variant="outline" size="sm" className="text-xs rounded-lg flex-1">CSV</Button>
                    <Button onClick={() => handleExport(type, 'pdf')} variant="outline" size="sm" className="text-xs rounded-lg flex-1">PDF</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {tab === 'revenue' && revenueReport && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <MetricCard label="Subscription Revenue" value={`₹${parseFloat(revenueReport.subscription_revenue || 0).toLocaleString()}`} icon={IndianRupee} color="green" />
            <MetricCard label="Interest Income" value={`₹${parseFloat(revenueReport.interest_income || 0).toLocaleString()}`} icon={TrendingUp} color="blue" />
            <MetricCard label="Repayments Collected" value={`₹${parseFloat(revenueReport.repayments_collected || 0).toLocaleString()}`} icon={Banknote} color="teal" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-[#0A2540] mb-4">Revenue Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subscription Revenue (mix of ₹899 / ₹1,299 plans)</span>
                <span className="font-bold">₹{parseFloat(revenueReport.subscription_revenue || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Interest Income (from EMI)</span>
                <span className="font-bold">₹{parseFloat(revenueReport.interest_income || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Repayments Collected</span>
                <span className="font-bold">₹{parseFloat(revenueReport.repayments_collected || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Total Disbursed (outflow)</span>
                <span className="font-bold text-red-600">- ₹{parseFloat(revenueReport.total_disbursed || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-3 bg-blue-50 rounded-xl px-4 mt-2">
                <span className="font-bold text-[#0A2540]">Net Revenue</span>
                <span className="font-black text-xl text-[#1565C0]">₹{parseFloat(revenueReport.net_revenue || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collections Tab */}
      {tab === 'collections' && collectionReport && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard label="Total Due" value={`₹${parseFloat(collectionReport.total_due || 0).toLocaleString()}`} icon={IndianRupee} color="gray" />
            <MetricCard label="Collected" value={`₹${parseFloat(collectionReport.collected || 0).toLocaleString()}`} icon={Banknote} color="green" />
            <MetricCard label="Pending" value={`₹${parseFloat(collectionReport.pending || 0).toLocaleString()}`} icon={Banknote} color="amber" />
            <MetricCard label="Overdue" value={`₹${parseFloat(collectionReport.overdue || 0).toLocaleString()}`} icon={AlertTriangle} color="red" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-[#0A2540] mb-4">Collection Rate</h3>
            <div className="flex items-center gap-4">
              <div className="text-5xl font-black text-[#1565C0]">{collectionReport.collection_rate || 0}%</div>
              <div className="text-sm text-gray-500">
                <p>{collectionReport.paid_count || 0} of {collectionReport.total_installments || 0} installments collected</p>
                <p className="text-amber-600">{collectionReport.pending_count || 0} pending</p>
                <p className="text-red-600">{collectionReport.overdue_count || 0} overdue</p>
              </div>
            </div>
            <div className="mt-4 bg-gray-100 rounded-full h-4 overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${collectionReport.collection_rate || 0}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Applications Tab */}
      {tab === 'applications' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <MetricCard label="Total" value={appReport?.total || 0} icon={FileText} color="gray" />
            <MetricCard label="Pending" value={appReport?.pending || 0} icon={FileText} color="amber" />
            <MetricCard label="Approved" value={appReport?.approved || 0} icon={FileText} color="green" />
            <MetricCard label="Rejected" value={appReport?.rejected || 0} icon={FileText} color="red" />
            <MetricCard label="Disbursed" value={appReport?.disbursed || 0} icon={FileText} color="blue" />
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-[#0A2540] mb-4">Total Amount Requested</h3>
            <p className="text-4xl font-black text-[#1565C0]">₹{parseFloat(appReport?.total_amount_requested || 0).toLocaleString()}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => handleExport('applications', 'csv')} variant="outline" className="rounded-xl"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            <Button onClick={() => handleExport('applications', 'pdf')} variant="outline" className="rounded-xl"><Download className="h-4 w-4 mr-2" /> Export PDF</Button>
          </div>
        </div>
      )}

      {/* Agents Tab */}
      {tab === 'agents' && agentReport && (
        <div className="space-y-6">
          {(agentReport.agents || []).length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No agent data available</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Agent</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Customers</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Applications</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Conversion</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agentReport.agents.map(a => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold">{a.name}</div>
                          <div className="text-xs text-gray-400">{a.email}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold">{a.total_customers}</td>
                        <td className="px-4 py-3">
                          <span className="font-semibold">{a.total_applications}</span>
                          <span className="text-xs text-green-600 ml-1">({a.approved_applications} approved)</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-bold ${a.conversion_rate > 50 ? 'text-green-600' : a.conversion_rate > 25 ? 'text-amber-600' : 'text-red-600'}`}>
                            {a.conversion_rate}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold">₹{parseFloat(a.total_commission || 0).toLocaleString()}</div>
                          <div className="text-xs text-gray-400">₹{parseFloat(a.paid_commission || 0).toLocaleString()} paid</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    red: "bg-red-50 text-red-600 border-red-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    teal: "bg-teal-50 text-teal-600 border-teal-100",
    gray: "bg-gray-50 text-gray-600 border-gray-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color] || colorMap.gray}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <p className="text-xl font-black">{value}</p>
    </div>
  );
}

function StatBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total * 100) : 0;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-600">{label}</span>
        <span className="text-sm font-bold">{count}</span>
      </div>
      <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
